/**
 * PoolReserveForDriverDialog — réserver pour le compte d'un tiers (lot 1.6).
 * =============================================================================
 * Un agent sans smartphone (15 % au CD31 selon la spec) n'a pas d'écran pour
 * réserver lui-même : c'est le gestionnaire qui le fait au guichet, pour lui.
 *
 * Contrat : `poolManagerReservationAPI.reserveForDriver` (`api/pool.js`) →
 * `POST /pool/reservations` → `backend/modules/pool/manager_reservation_service.mjs`
 * → RÉUTILISE `createReservation` (self-service), donc les MÊMES refus, avec le
 * MÊME contrat d'erreur (`code`, `data.next_actions`) que l'app conducteur.
 *
 * ⚠️ NON MONTÉ par ce lot (voir le rapport) : à brancher là où un gestionnaire
 * doit pouvoir réserver pour un tiers (vue Opérations, fiche conducteur…).
 *
 * ——— Ce que le formulaire fait, et pourquoi (2026-09-14) ———————————————————
 * Le dialogue ne propose QUE les véhicules libres sur le créneau saisi
 * (`poolAvailabilityAPI.get`, `state === 'available'`). Avant, il listait tout
 * le parc et laissait le serveur refuser en `VEHICLE_BUSY` : le gestionnaire
 * faisait le travail deux fois, et « tirait à côté » plusieurs fois de suite.
 * Retirer l'option indisponible économise le signal — l'autorité reste au
 * serveur, qui retranche au moment du POST ce qui a été pris entre-temps.
 *
 * Conséquence sur l'ordre des champs : le créneau vient AVANT le véhicule,
 * puisque la liste des véhicules en dépend. Conducteur → Créneau → Véhicule →
 * Destination.
 *
 * ——— Pourquoi il n'y a plus de réservation « par classe » (2026-09-14) —————
 * Ce dialogue proposait un mode « Une classe de véhicule » qui envoyait
 * `vehicle_class` au serveur SANS choisir de véhicule : le module, par
 * conception, n'attribue le véhicule qu'à la prise en main. Testé en démo le
 * 14/09, Jean-Guy a choisi VP et obtenu une réservation FANTÔME — invisible
 * dans la grille, « véhicule non identifié » au panneau de garde — parce que
 * `vehicle_id` restait `null` jusqu'à la prise. Décision : côté gestionnaire,
 * une réservation faite au guichet porte TOUJOURS un véhicule précis. Le
 * remplaçant honnête du mode classe est le filtre Catégorie (qui réduit la
 * liste des véhicules libres) + le bouton « Le premier libre » (qui choisit
 * MAINTENANT le premier véhicule filtré, visible avant de valider). Le
 * self-service conducteur, lui, garde sa réservation par classe : un agent
 * qui vient chercher un véhicule au dépôt prend ce qu'on lui attribue sur
 * place, ce n'est pas un fantôme pour lui.
 *
 * Échelle visée : 900 véhicules / 1 000 conducteurs. Un `<select>` de mille
 * noms est inutilisable — chaque liste est donc précédée d'un champ de
 * recherche qui filtre ses options, et le rendu est borné à
 * `MAX_RENDERED_OPTIONS` (au-delà, on demande d'affiner). On garde le `<select>`
 * lui-même : validation clavier et accessibilité natives, gratuitement.
 *
 * ——— Limites connues ————————————————————————————————————————————————————————
 * - La disponibilité est une PHOTO du créneau au moment de la requête : deux
 *   gestionnaires côte à côte peuvent encore viser le même véhicule. Le refus
 *   serveur reste le juge, il est affiché tel quel (avec ses `next_actions`).
 * - Le filtre `siteId` est une CONVENANCE d'affichage, pas une garantie :
 *   l'autorité est `requireDriverInScope` / `requireVehicleInScope` côté
 *   serveur, qui répond 404 « introuvable » à un id forgé hors périmètre.
 * - La liste des conducteurs n'est pas filtrée par disponibilité : le module ne
 *   dit pas (encore) si un agent détient déjà un véhicule sur le créneau.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X, User, Car, Calendar, AlertTriangle, Search, Info } from 'lucide-react';
import { poolManagerReservationAPI, poolAvailabilityAPI } from '../../api/pool.js';
import { driverAPI } from '../../api/drivers.js';
import { useWorkingSites } from '../../index.js';
import PoolDestinationPicker from './PoolDestinationPicker.jsx';

/** Au-delà, on ne rend rien de plus : mille `<option>` ne se lisent pas, elles se cherchent. */
const MAX_RENDERED_OPTIONS = 200;

/** `<input type="datetime-local">` ne connaît pas les fuseaux : aller-retour local, comme `PoolAvailabilityPanel`. */
function toLocalInputValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
function fromLocalInputValue(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function defaultStart() {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 15, 0, 0);
  return d;
}
function defaultEnd(start) {
  return new Date(start.getTime() + 2 * 60 * 60_000);
}

const driverLabel = (d) => [d.first_name, d.last_name].filter(Boolean).join(' ').trim() || `#${d.id}`;
/** La disponibilité rend `vehicle_id` (pas `id`) : le libellé lit les deux formes. */
const vehicleLabel = (v) => [v.license_plate, v.make, v.model].filter(Boolean).join(' — ') || `#${v.vehicle_id ?? v.id}`;

/** Recherche « à la main » : insensible à la casse et aux accents, sur la concaténation des champs utiles. */
const normalize = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const matches = (haystack, needle) => normalize(haystack).includes(normalize(needle));

/**
 * @param {Object} props
 * @param {() => void} props.onClose
 * @param {(reservation: object) => void} [props.onCreated]
 * @param {number|string|null} [props.siteId] - présélectionne/filtre au périmètre affiché (convenance UI ; l'autorité reste côté serveur)
 * @param {number|string|null} [props.initialVehicleId] - véhicule présélectionné (clic sur la grille hebdomadaire)
 * @param {string|null} [props.initialStart] - début proposé, ISO 8601
 * @param {string|null} [props.initialEnd] - fin proposée, ISO 8601
 */
function PoolReserveForDriverDialog({ onClose, onCreated, siteId = null, initialVehicleId = null, initialStart = null, initialEnd = null }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Les unités territoriales servent de « lieux fréquents » au sélecteur de destination.
  const { data: workingSites = [] } = useWorkingSites();

  const [driverId, setDriverId] = useState('');
  const [driverSearch, setDriverSearch] = useState('');
  // Un clic sur la grille hebdomadaire ouvre le dialogue sur CE véhicule et CE créneau :
  // le gestionnaire n'a plus qu'à choisir l'agent.
  const [vehicleId, setVehicleId] = useState(initialVehicleId != null ? String(initialVehicleId) : '');
  const [vehicleSearch, setVehicleSearch] = useState('');
  // Filtre de CONFORT sur la liste des véhicules libres — jamais envoyé au serveur.
  // '' = toutes catégories.
  const [vehicleCategory, setVehicleCategory] = useState('');
  // Le véhicule qu'on a dû RETIRER de la sélection parce que le créneau a bougé :
  // on ne vide pas un choix en silence, sinon le gestionnaire cherche son erreur.
  const [droppedVehicle, setDroppedVehicle] = useState(null);
  // La destination porte désormais un point OPTIONNEL : sans coordonnées, le
  // panneau de garde ne peut pas placer l'arrivée sur la carte.
  const [destination, setDestination] = useState({ label: '', lat: null, lng: null });
  const [start, setStart] = useState(() => (initialStart ? new Date(initialStart) : defaultStart()));
  const [end, setEnd] = useState(() => (initialEnd ? new Date(initialEnd) : defaultEnd(initialStart ? new Date(initialStart) : defaultStart())));

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const startIso = useMemo(() => (start instanceof Date ? start.toISOString() : start), [start]);
  const endIso = useMemo(() => (end instanceof Date ? end.toISOString() : end), [end]);
  const rangeIsValid = Boolean(startIso && endIso) && new Date(endIso) > new Date(startIso);

  // `driverAPI.getAll` passe déjà `_limit=5000` : sans lui, le data layer tronque
  // à 100 lignes SANS LE DIRE, et l'agent cherché n'est simplement pas dans la liste.
  const driversQuery = useQuery({
    queryKey: ['pool', 'reserve-for-driver', 'drivers'],
    queryFn: () => driverAPI.getAll(),
    staleTime: 60_000,
  });

  // Une requête par créneau : c'est le créneau qui définit ce qui est libre.
  // Tant que la fin n'est pas après le début, il n'y a rien à demander.
  const availabilityQuery = useQuery({
    queryKey: ['pool', 'availability', startIso, endIso, siteId ?? null],
    queryFn: () => poolAvailabilityAPI.get({ from: startIso, to: endIso, siteId: siteId ?? undefined }),
    enabled: rangeIsValid,
    staleTime: 15_000,
  });

  const drivers = useMemo(() => {
    const res = driversQuery.data;
    return res?.data || (Array.isArray(res) ? res : []);
  }, [driversQuery.data]);

  const availabilityVehicles = useMemo(() => {
    const res = availabilityQuery.data;
    return res?.data?.vehicles || res?.vehicles || [];
  }, [availabilityQuery.data]);

  const availableVehicles = useMemo(
    () => availabilityVehicles.filter((v) => v.state === 'available'),
    [availabilityVehicles]
  );

  const loadError = driversQuery.error?.message || availabilityQuery.error?.message || null;
  const loadingDrivers = driversQuery.isLoading;
  const loadingVehicles = rangeIsValid && availabilityQuery.isLoading;

  // Filtre de CONVENANCE côté conducteurs : ne montrer que le périmètre affiché.
  // (Les véhicules, eux, sont déjà bornés au site par le serveur via `siteId`.)
  const scopedDrivers = useMemo(
    () => (siteId ? drivers.filter((d) => String(d.working_site_id) === String(siteId)) : drivers),
    [drivers, siteId]
  );

  const filteredDrivers = useMemo(() => {
    const q = driverSearch.trim();
    if (!q) return scopedDrivers;
    return scopedDrivers.filter((d) => matches(driverLabel(d), q));
  }, [scopedDrivers, driverSearch]);

  // Les catégories ne sortent QUE des véhicules libres, avec leur compte : le
  // gestionnaire voit tout de suite s'il reste des VP avant de filtrer dessus.
  const categoryOptions = useMemo(() => {
    const counts = new Map();
    for (const v of availableVehicles) {
      if (!v.vehicle_class) continue;
      counts.set(v.vehicle_class, (counts.get(v.vehicle_class) || 0) + 1);
    }
    return [...counts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [availableVehicles]);

  // Le filtre catégorie s'applique AVANT la recherche texte : les deux se combinent.
  const availableVehiclesInCategory = useMemo(() => {
    if (!vehicleCategory) return availableVehicles;
    return availableVehicles.filter((v) => v.vehicle_class === vehicleCategory);
  }, [availableVehicles, vehicleCategory]);

  const filteredVehicles = useMemo(() => {
    const q = vehicleSearch.trim();
    if (!q) return availableVehiclesInCategory;
    return availableVehiclesInCategory.filter((v) => matches(`${vehicleLabel(v)} ${v.vehicle_class || ''}`, q));
  }, [availableVehiclesInCategory, vehicleSearch]);

  // Le créneau a bougé et le véhicule choisi n'est plus libre : on le retire de la
  // sélection ET on le dit. Laisser l'option soumettable rendrait le refus serveur.
  useEffect(() => {
    if (!vehicleId || !availabilityQuery.data) return;
    if (availableVehicles.some((v) => String(v.vehicle_id) === String(vehicleId))) return;
    const known = availabilityVehicles.find((v) => String(v.vehicle_id) === String(vehicleId));
    setDroppedVehicle(known ? vehicleLabel(known) : `#${vehicleId}`);
    setVehicleId('');
  }, [vehicleId, availableVehicles, availabilityVehicles, availabilityQuery.data]);

  // Une catégorie qui n'a plus de véhicule libre ne doit pas rester sélectionnée.
  useEffect(() => {
    if (!vehicleCategory || !availabilityQuery.data) return;
    if (!categoryOptions.some((c) => c.name === vehicleCategory)) setVehicleCategory('');
  }, [vehicleCategory, categoryOptions, availabilityQuery.data]);

  // Quand la recherche ne laisse qu'un candidat, on le choisit : le gestionnaire a
  // déjà dit lequel en tapant sa plaque.
  useEffect(() => {
    if (driverSearch.trim() && filteredDrivers.length === 1) setDriverId(String(filteredDrivers[0].id));
  }, [driverSearch, filteredDrivers]);

  useEffect(() => {
    if (vehicleSearch.trim() && filteredVehicles.length === 1) {
      setDroppedVehicle(null);
      setVehicleId(String(filteredVehicles[0].vehicle_id));
    }
  }, [vehicleSearch, filteredVehicles]);

  const shownDrivers = filteredDrivers.slice(0, MAX_RENDERED_OPTIONS);
  const shownVehicles = filteredVehicles.slice(0, MAX_RENDERED_OPTIONS);

  const canSubmit = driverId && vehicleId && rangeIsValid && !submitting;

  const pickFirstAvailable = () => {
    if (filteredVehicles.length === 0) return;
    setDroppedVehicle(null);
    setVehicleId(String(filteredVehicles[0].vehicle_id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!rangeIsValid) {
      setSubmitError({ message: t('poolManagerReservation.errors.endBeforeStart'), nextActions: null });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        driver_id: driverId,
        vehicle_id: vehicleId,
        planned_start: startIso,
        planned_end: endIso,
        destination_label: destination.label || undefined,
      };
      // Les coordonnées ne partent QUE si un point a été posé : une clé à `null`
      // écraserait un point existant côté serveur.
      if (typeof destination.lat === 'number' && typeof destination.lng === 'number') {
        payload.destination_lat = destination.lat;
        payload.destination_lng = destination.lng;
      }
      const res = await poolManagerReservationAPI.reserveForDriver(payload);
      const reservation = res?.data?.reservation || res?.reservation;
      // La fiche de l'agent et les listes doivent changer d'état TOUT DE SUITE
      // (retour JGS 14/09), pas au prochain tic de 60 s.
      queryClient.invalidateQueries({ queryKey: ['poolPresence'] });
      queryClient.invalidateQueries({ queryKey: ['poolCustody'] });
      queryClient.invalidateQueries({ queryKey: ['pool', 'availability'] });
      onCreated?.(reservation);
      onClose();
    } catch (err) {
      // `core.js` garde désormais le message du module (`err.message`) et ses
      // `next_actions` (`err.next_actions`) — corrigé le 2026-09-13.
      const message = err.message || err.details || t('poolManagerReservation.errors.generic');
      const nextActions = err.next_actions || err.data?.next_actions || err.details?.next_actions || null;
      setSubmitError({ message, nextActions });
    } finally {
      setSubmitting(false);
    }
  };

  const searchInputClass = 'w-full rounded-lg border border-gray-300 py-1.5 pl-8 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500';
  const selectClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="max-h-[90vh] w-[520px] max-w-full overflow-y-auto rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between rounded-t-xl bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-4">
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-white" />
            <h2 className="text-lg font-semibold text-white">
              {t('poolManagerReservation.title')}
            </h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" aria-label={t('common.actions.close')}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <p className="text-sm text-gray-500">
            {t('poolManagerReservation.subtitle')}
          </p>

          {loadError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {loadError}
            </div>
          )}

          {/* 1. Conducteur — recherche puis liste */}
          <div>
            <span className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
              <User className="h-3.5 w-3.5" />
              {t('poolManagerReservation.fields.driver')}
            </span>
            <div className="relative mb-1.5">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={driverSearch}
                onChange={(e) => setDriverSearch(e.target.value)}
                placeholder={t('poolManagerReservation.fields.driverSearchPlaceholder')}
                aria-label={t('poolManagerReservation.fields.driverSearchPlaceholder')}
                className={searchInputClass}
              />
            </div>
            <select
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              disabled={loadingDrivers}
              required
              aria-label={t('poolManagerReservation.fields.driver')}
              className={selectClass}
            >
              <option value="">{t('poolManagerReservation.fields.driverPlaceholder')}</option>
              {shownDrivers.map((d) => (
                <option key={d.id} value={d.id}>{driverLabel(d)}</option>
              ))}
            </select>
            {filteredDrivers.length > MAX_RENDERED_OPTIONS && (
              <p className="mt-1 text-xs text-amber-700">
                {t('poolManagerReservation.hints.tooManyOptions', { shown: MAX_RENDERED_OPTIONS, total: filteredDrivers.length })}
              </p>
            )}
          </div>

          {/* 2. Créneau — AVANT le véhicule : c'est lui qui décide de ce qui est libre */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
                <Calendar className="h-3.5 w-3.5" />
                {t('poolManagerReservation.fields.start')}
              </span>
              <input
                type="datetime-local"
                value={toLocalInputValue(start)}
                onChange={(e) => {
                  const iso = fromLocalInputValue(e.target.value);
                  if (iso) setStart(new Date(iso));
                }}
                required
                aria-label={t('poolManagerReservation.fields.start')}
                className={selectClass}
              />
            </label>
            <label className="block">
              <span className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
                <Calendar className="h-3.5 w-3.5" />
                {t('poolManagerReservation.fields.end')}
              </span>
              <input
                type="datetime-local"
                value={toLocalInputValue(end)}
                onChange={(e) => {
                  const iso = fromLocalInputValue(e.target.value);
                  if (iso) setEnd(new Date(iso));
                }}
                required
                aria-label={t('poolManagerReservation.fields.end')}
                className={selectClass}
              />
            </label>
          </div>

          {!rangeIsValid && (
            <p className="text-xs text-amber-700">{t('poolManagerReservation.errors.endBeforeStart')}</p>
          )}

          {/* 3. Véhicule — uniquement ce qui est LIBRE sur le créneau, filtrable par catégorie */}
          <div>
            <span className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
              <Car className="h-3.5 w-3.5" />
              {t('poolManagerReservation.fields.vehicle')}
            </span>

            <select
              value={vehicleCategory}
              onChange={(e) => setVehicleCategory(e.target.value)}
              disabled={loadingVehicles || !rangeIsValid}
              aria-label={t('poolManagerReservation.fields.category')}
              className={`${selectClass} mb-1.5`}
            >
              <option value="">{t('poolManagerReservation.fields.categoryAllOption')}</option>
              {categoryOptions.map((c) => (
                <option key={c.name} value={c.name}>
                  {t('poolManagerReservation.fields.categoryOption', { name: c.name, count: c.count })}
                </option>
              ))}
            </select>

            <div className="relative mb-1.5">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={vehicleSearch}
                onChange={(e) => setVehicleSearch(e.target.value)}
                placeholder={t('poolManagerReservation.fields.vehicleSearchPlaceholder')}
                aria-label={t('poolManagerReservation.fields.vehicleSearchPlaceholder')}
                className={searchInputClass}
              />
            </div>
            <div className="flex gap-2">
              <select
                value={vehicleId}
                onChange={(e) => { setDroppedVehicle(null); setVehicleId(e.target.value); }}
                disabled={loadingVehicles || !rangeIsValid}
                required
                aria-label={t('poolManagerReservation.fields.vehicle')}
                className={`${selectClass} flex-1`}
              >
                <option value="">{t('poolManagerReservation.fields.vehiclePlaceholder')}</option>
                {shownVehicles.map((v) => (
                  <option key={v.vehicle_id} value={v.vehicle_id}>{vehicleLabel(v)}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={pickFirstAvailable}
                disabled={loadingVehicles || !rangeIsValid || filteredVehicles.length === 0}
                className="shrink-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
              >
                {t('poolManagerReservation.fields.pickFirstAvailable')}
              </button>
            </div>
            {filteredVehicles.length > MAX_RENDERED_OPTIONS && (
              <p className="mt-1 text-xs text-amber-700">
                {t('poolManagerReservation.hints.tooManyOptions', { shown: MAX_RENDERED_OPTIONS, total: filteredVehicles.length })}
              </p>
            )}

            {/* Le compte, sous le champ : « ce que je vois » et « sur quoi c'est pris ». */}
            {rangeIsValid && !loadingVehicles && availabilityQuery.data && (
              <p className="mt-1 text-xs text-gray-500">
                {vehicleCategory
                  ? t('poolManagerReservation.availability.freeInCategory', {
                      count: availableVehiclesInCategory.length,
                      category: vehicleCategory,
                    })
                  : t('poolManagerReservation.availability.free', {
                      count: availableVehicles.length,
                      total: availabilityVehicles.length,
                    })}
              </p>
            )}
            {loadingVehicles && (
              <p className="mt-1 text-xs text-gray-400">{t('poolManagerReservation.availability.loading')}</p>
            )}

            {droppedVehicle && (
              <p className="mt-1.5 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{t('poolManagerReservation.availability.pickedVehicleBusy', { vehicle: droppedVehicle })}</span>
              </p>
            )}
          </div>

          {/* 4. Destination — texte libre + point géolocalisé optionnel */}
          <PoolDestinationPicker
            value={destination}
            onChange={setDestination}
            sites={workingSites}
          />

          {submitError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <p className="flex items-start gap-1.5">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{submitError.message}</span>
              </p>
              {Array.isArray(submitError.nextActions) && submitError.nextActions.length > 0 && (
                <ul className="mt-1.5 list-inside list-disc space-y-0.5 pl-1">
                  {submitError.nextActions.map((a, i) => (
                    <li key={i}>{a.label || a.kind}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {submitting
                ? t('poolManagerReservation.actions.submitting')
                : t('poolManagerReservation.actions.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PoolReserveForDriverDialog;
